import React from 'react';
import { Avatar, Chip, Icon, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { V4_TABLE } from '../../theme/v4Compat';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useAuthContext from '../../hooks/useAuthContext';

import { Loader, Title, LegacyGrid as Grid } from '../../elements';
import {
  AnimalGender,
  AnimalTrendDataType,
  AnimalType,
  AnimalWeightCategoryInfo,
  WeightType
} from '../../models/animal';
import {
  AnimalTrendData,
  AnimalUpdateModal,
  AnimalViewNotes,
  AnimalViewPiglets,
  AnimalViewFosterMotherTransfers,
  AnimalViewFosterMotherHistory,
  AnimalViewWeightsAnalyticsGraph,
  InjectionHistoryView,
  AnimalLocationHistoryTimeline,
  AnimalViewExternalIds,
  AnimalViewHealthLogs,
  AnimalPhotosIndicator,
  AnimalFeedTab,
  DigitalTwinModule
} from '../../components';
import { colors, convertWeight, extractApiErrorMessage, notifications } from '../../utils';
import UnknownIco from '../../static/unknown01.jpg';
import SowIco from '../../static/sow01.jpg';
import BoarIco from '../../static/boar01.svg';
import GiltIco from '../../static/gilt01.jpg';
import PigletIco from '../../static/piglet01.jpg';
import FatteningIco from '../../static/fatt01.jpg';
import CowIco from '../../static/cow01.jpg';
import CalfIco from '../../static/calf01.jpg';
import GoatIco from '../../static/goat01.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteIcon from '@mui/icons-material/Delete';
import { faMapMarkerAlt, faMars, faVenus, faVenusMars } from '@fortawesome/free-solid-svg-icons';
import { DEFAULT_ROWS_PER_PAGE_MAX, LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import AnimalViewSlaughterHouse from '../../components/AnimalViewSlaughterhouse';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import type { AnimalLocation, AnimalObservation, AnimalView as AnimalViewModel } from '../../models/animal-view';
import { toAnimalObservation, toAnimalView } from '../../models/animal-view';
import { AnimalObservationCategory, observationLabel } from '../../utils/animalObservations';
import {
  AnimalExternalIdentifierTypeLabelKeys,
  AnimalExternalIdentifierViewModel,
  findPlasticNumber
} from '../../models/animal-external-identifier';
import { renderWeightCellDoubleWeightWithDate, AnimalWeightCategoryLabelKeys } from '../../utils/animalutils';
import { AnimalViewTabRail } from './AnimalViewTabRail';
import { DigitalTwinTabEntryCard } from './DigitalTwinTabEntryCard';

// A biter is a problem animal and a victim is an unhealthy one, so the two bite categories read
// hotter than a genetic defect.
const OBSERVATION_CHIP_CLASSES: Record<string, string> = {
  [AnimalObservationCategory.GeneticFailure]: '!bg-amber-100 !text-amber-900',
  [AnimalObservationCategory.Biter]: '!bg-red-100 !text-red-900',
  [AnimalObservationCategory.BiteVictim]: '!bg-orange-100 !text-orange-900'
};

function getConvertWeight(weight: AnimalWeightCategoryInfo | null | undefined): AnimalWeightCategoryInfo | null {
  if (weight?.value == null) {
    return null;
  }

  return {
    value: convertWeight(weight?.value, weight?.unit, 1) ?? 0,
    unit: 1
  };
}

const AnimalView: React.FC<any> = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();

  const apiService = new ApiService();

  const [animalType, setAnimalType] = React.useState(0);
  const [animal, setAnimal] = React.useState<AnimalViewModel | null>(null);
  const [antibioticFree, setAntibioticFree] = React.useState(null as boolean | null | undefined);

  const [loaded, setLoaded] = React.useState(false);
  const [shouldReloadData, setShouldReloadData] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(true);
  const [searchParams] = useSearchParams();
  // `?tab=feed` lets another feed view open this animal straight on its feed history.
  const [currentTab, setCurrentTab] = React.useState(() => (searchParams.get('tab') === 'feed' ? 'feed' : 'overview'));
  // Held here rather than inside AnimalTrendData: that component is unmounted by the tab switch and
  // by every animal reload, which reset the selection back to the initial type.
  const [animalTrendDataType, setAnimalTrendDataType] = React.useState<AnimalTrendDataType>(
    AnimalTrendDataType.SlaughterhouseDataWeight
  );
  const [projectedSlaughter, setProjectedSlaughter] = React.useState<{
    at: string;
    isEstimated: boolean;
  } | null>(null);
  // The animal payload does not carry the plastic ear tag number: it only exists as an external
  // identifier of type PlasticNumber, so it is fetched once per animal (AB#3739). Empty string means
  // "this animal has no plastic number" - it must never fall back to the animal/RFID number.
  const [plasticNumber, setPlasticNumber] = React.useState('');
  // Non-fatal conditions registered against this animal - genetic failures, biters, bite victims
  // (AB#3724). They hang off the animal rather than off a mortality event, so they need their own
  // request; the animal payload does not carry them.
  const [observations, setObservations] = React.useState<AnimalObservation[]>([]);

  // Get farm weight unit from context

  const resetValues = () => {
    setAnimalType(0);
    setAnimal(null);
  };

  const loadAnimalData = async (withReset: boolean = true) => {
    if (withReset) {
      resetValues();
    }

    setIsBusy(true);
    setLoaded(false);

    if (!id) {
      notifications.error('Animal ID is undefined.');
      return;
    }

    await apiService.api.animals
      .animalsGetAnimalById({ Id: id })
      .then((result) => {
        setAnimal(toAnimalView(result.data));
        setAnimalType(result.data.animalType);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        setLoaded(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const loadAntibioticFreeStatus = async () => {
    if (!id) {
      notifications.error('Animal ID is undefined.');
      return;
    }

    await apiService.api.animals
      .animalsGetAnimalAntibioticFreeResult({ Id: id })
      .then((result) => {
        setAntibioticFree(result.data.result);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const deleteAnimalNote = async (noteId: string) => {
    apiService.api.animals
      .animalsDeleteAnimalNote({ NoteId: noteId })
      .then(() => {
        notifications.success('Animal note deleted successfully');
        setShouldReloadData(true);
      })
      .catch((e) => {
        notifications.error(e?.error?.message ?? e.toString());
      });
  };

  const addAnimalNote = async (text: string) => {
    if (!id) {
      notifications.error('Animal ID is undefined.');
      return;
    }

    apiService.api.animals
      .animalsCreateAnimalNote({ animalId: id, text: text })
      .then(() => {
        notifications.success(t('f-animal-note-added'));
        setShouldReloadData(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const renderGenderIcon = (gender: AnimalGender): JSX.Element => {
    switch (gender) {
      case AnimalGender.Male:
        return <FontAwesomeIcon icon={faMars} />;
      case AnimalGender.Female:
        return <FontAwesomeIcon icon={faVenus} />;
      default:
        return <FontAwesomeIcon icon={faVenusMars} />;
    }
  };

  const renderNipplesCell = (animal: AnimalViewModel) => {
    if (!animal.nipples) {
      return '-';
    }

    return (
      <Grid container direction="column" spacing={1}>
        <Grid item container spacing={1}>
          <Grid item md="auto" xs={12}>
            <Typography variant="caption" sx={{ display: 'block' }} gutterBottom>
              {t('f-animal-nipples-before-bb')}
            </Typography>
          </Grid>
          <Grid item md="auto" xs={6}>
            <Chip size="small" variant="outlined" avatar={<Avatar>L</Avatar>} label={animal.nipples.left} />{' '}
          </Grid>
          <Grid item md="auto" xs={6}>
            <Chip size="small" variant="outlined" avatar={<Avatar>R</Avatar>} label={animal.nipples.right} />
          </Grid>
        </Grid>

        <Grid item container spacing={1}>
          <Grid item md="auto" xs={12}>
            <Typography variant="caption" sx={{ display: 'block' }} gutterBottom>
              {t('f-animal-nipples-after-bb')}
            </Typography>
          </Grid>
          <Grid item md="auto" xs={6}>
            <Chip
              size="small"
              variant="outlined"
              avatar={<Avatar>L</Avatar>}
              label={animal.nipples.additionalLeft}
            />{' '}
          </Grid>
          <Grid item md="auto" xs={6}>
            <Chip size="small" variant="outlined" avatar={<Avatar>R</Avatar>} label={animal.nipples.additionalRight} />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderAntibioticFreeCell = () => {
    let icon = 'help';
    let text = '?';
    let chipColour: string | undefined = undefined;

    if (antibioticFree) {
      icon = 'check';
      text = t('f-animal-antibiotic-free-ok');
      chipColour = colors.green;
    } else {
      icon = 'medication';
      text = t('f-animal-antibiotic-free-false');
      chipColour = colors.red;
    }

    return (
      <Chip
        // v4 left a font icon at 24px in a small chip; v9 shrinks it to 18px.
        sx={{
          '& .MuiChip-icon': { fontSize: 24 },
          ...(chipColour ? { color: chipColour, borderColor: chipColour } : {})
        }}
        size="small"
        variant="outlined"
        icon={<Icon>{icon}</Icon>}
        label={text}
      />
    );
  };

  // Every registration is its own chip: repeat observations on one animal are kept separately, each
  // with its own timestamp, rather than collapsed into a single flag.
  const renderObservationsCell = () => {
    if (observations.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          {t('f-animal-observations-none')}
        </Typography>
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {observations.map((observation) => {
          const details = [
            `${t('f-animal-observation-registered-at')}: ${formatDate(observation.registeredAt)}`,
            observation.registeredBy ? `${t('f-animal-observation-registered-by')}: ${observation.registeredBy}` : null,
            observation.location ? `${t('f-animal-view-location')}: ${observation.location}` : null,
            observation.comment
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <Tooltip key={observation.id} title={details}>
              <Chip
                size="small"
                label={`${observationLabel(t, observation)} (${formatDate(observation.registeredAt)})`}
                className={OBSERVATION_CHIP_CLASSES[observation.category] ?? '!bg-gray-100 !text-gray-800'}
              />
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const renderAnimalTypeAvatar = (): JSX.Element => {
    switch (animalType) {
      case AnimalType.Sow:
      case AnimalType.Boar:
        return <Avatar alt="Sow type" src={SowIco} />;
      case AnimalType.Gilt:
        return <Avatar alt="Gilt type" src={GiltIco} />;
      case AnimalType.Piglet:
        return <Avatar alt="Piglet type" src={PigletIco} />;
      case AnimalType.Fattening:
        return <Avatar alt="Fattening type" src={FatteningIco} />;
      case AnimalType.Goat:
        return <Avatar alt="Goat type" src={GoatIco} />;
      case AnimalType.Cow:
        return <Avatar alt="Cow type" src={CowIco} />;
      case AnimalType.Calf:
        return <Avatar alt="Calf type" src={CalfIco} />;
      default:
        return <Avatar alt="Animal type" src={UnknownIco} />;
    }
  };

  const renderAnimalIsDied = (animal: AnimalViewModel) => {
    const parts: string[] = [];

    if (animal.disease?.name) {
      parts.push(animal.disease.name);
    }

    if (animal.mortalityReason?.name) {
      parts.push(animal.mortalityReason.name);
    }

    const label = animal.deathDate
      ? `${t('f-search-view-died')} (${formatDate(animal.deathDate)})`
      : t('f-search-view-died');

    return `${label}${parts.length > 0 ? ': ' + parts.join(', ') : ''}`;
  };

  React.useEffect(() => {
    if (shouldReloadData) {
      const loadData = async () => loadAnimalData(false);

      loadData();
      setShouldReloadData(false);
    }
  }, [shouldReloadData]);

  React.useEffect(() => {
    const loadData = async () => loadAnimalData();
    loadData();

    const loadAntibioticFree = async () => loadAntibioticFreeStatus();
    loadAntibioticFree();
  }, [id]);

  // One request per animal - not per render and not per identifier row.
  React.useEffect(() => {
    setPlasticNumber('');

    if (!id) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await apiService.api.animals.animalsGetExternalIdentifiers({
          id,
          pageIndex: 1,
          pageSize: DEFAULT_ROWS_PER_PAGE_MAX
        });

        // The generated client has no `type` on the identifier view model yet - see
        // models/animal-external-identifier.ts. Drop the cast once `bun run swagger` is re-run.
        const identifiers = (result.data.items ?? []) as AnimalExternalIdentifierViewModel[];

        if (!cancelled) {
          setPlasticNumber(findPlasticNumber(identifiers));
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setPlasticNumber('');
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // One request per animal. Registration happens on mobile, so there is nothing on this page that
  // can invalidate the list mid-visit.
  React.useEffect(() => {
    setObservations([]);

    if (!id) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await apiService.api.animals.animalsGetObservations({
          id,
          pageIndex: 1,
          pageSize: DEFAULT_ROWS_PER_PAGE_MAX
        });

        if (!cancelled) {
          setObservations((result.data.items ?? []).map(toAnimalObservation));
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setObservations([]);
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  React.useEffect(() => {
    if (!loaded || !id) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getProjectedSlaughterDate(id);
        if (!cancelled) {
          setProjectedSlaughter({
            at: new Date(data.projectedSlaughterDate).toLocaleString(),
            isEstimated: data.isEstimated
          });
        }
      } catch {
        if (!cancelled) {
          setProjectedSlaughter(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loaded, id]);

  const renderLocation = (location: AnimalLocation) => {
    if (location === null) {
      return '';
    }

    let text = '';

    if (location.room !== null) text += t('f-loc-room') + ': ' + location.room;
    if (location.pen !== null) text += ' ' + t('f-loc-pen') + ': ' + location.pen;
    if (location.number !== null) text += ' ' + t('f-number') + ': ' + location.number;

    return text;
  };

  const handleTabChange = (_event: React.ChangeEvent<any>, newValue: string) => {
    setCurrentTab(newValue);
  };

  const hasDigitalTwin =
    animal?.animalType === AnimalType.Piglet ||
    animal?.animalType === AnimalType.Fattening ||
    animal?.animalType === AnimalType.Sow;

  const attachDigitalTwinEntry = hasDigitalTwin && currentTab === 'digitalTwin';

  const scrollToDigitalTwinForecast = React.useCallback(() => {
    document.getElementById('digital-twin-forecast-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Sort notes by created date DESC
  const sortedNotes = React.useMemo(() => {
    if (!animal?.notes) return [];
    return [...animal.notes].sort((a, b) => {
      const dateA = new Date(a.created ?? 0).getTime();
      const dateB = new Date(b.created ?? 0).getTime();
      return dateB - dateA;
    });
  }, [animal?.notes]);

  const formattedDate = (date: string | null): string => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {loaded && animal && (
          <Grid item xs={12}>
            <AnimalViewTabRail
              value={currentTab}
              onChange={handleTabChange}
              hasDigitalTwin={hasDigitalTwin}
              attachEntryCardBelow={attachDigitalTwinEntry}
              t={t}
            />
            {!attachDigitalTwinEntry && <Divider />}

            {currentTab === 'overview' && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid container direction="row" alignItems="center" spacing={1}>
                  <Grid item id="animalTypeIcon">
                    {renderAnimalTypeAvatar()}
                  </Grid>
                  <Grid item>
                    <Title>{AnimalType[animal.animalType]}</Title>
                  </Grid>

                  <Grid item>
                    {animal.isDied && (
                      <Chip
                        color="secondary"
                        sx={{ m: 1 }}
                        size="small"
                        label={renderAnimalIsDied(animal)}
                        variant="outlined"
                      />
                    )}
                    {animal.isSoftDeleted && (
                      <Chip
                        sx={{ m: 1 }}
                        size="small"
                        avatar={<DeleteIcon />}
                        label={t('f-search-view-deleted')}
                        variant="outlined"
                      />
                    )}
                    {animal.motherId && (
                      <Chip
                        sx={{ m: 1 }}
                        avatar={<Avatar src={SowIco} />}
                        label={t('f-animal-view-mom')}
                        color="primary"
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          navigate(`/animal/${animal.motherId}`);
                        }}
                      />
                    )}
                    {animal.fatherId && (
                      <Chip
                        sx={{ m: 1 }}
                        avatar={<Avatar src={BoarIco} />}
                        label={t('f-animal-view-father')}
                        color="primary"
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          navigate(`/animal/${animal.fatherId}`);
                        }}
                      />
                    )}
                    {animal.fosterMotherId && (
                      <Chip
                        sx={{ m: 1 }}
                        avatar={<Avatar src={SowIco} />}
                        label={t('f-animal-view-mom-foster')}
                        color="primary"
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          navigate(`/animal/${animal.fosterMotherId}`);
                        }}
                      />
                    )}
                    <AnimalPhotosIndicator animalId={id as string} />
                  </Grid>

                  {(state.role === UserRole.manager || state.role === UserRole.admin) && (
                    <Grid item sx={{ textAlign: 'right', flex: 1 }}>
                      <AnimalUpdateModal
                        animalId={id as string}
                        onAnimalUpdated={() => {
                          setShouldReloadData(true);
                        }}
                      />
                    </Grid>
                  )}
                </Grid>

                <Grid item md={12}>
                  <Table size="small" sx={V4_TABLE} aria-label="animal data table" id="animal-data-table">
                    <TableBody>
                      <TableRow key={'animalNumber'}>
                        <TableCell align="right">{t('f-com-animal-number')}</TableCell>
                        <TableCell sx={BOLD}>{animal.number}</TableCell>
                      </TableRow>

                      <TableRow key={'animalPlasticNumber'}>
                        <TableCell align="right">{t(AnimalExternalIdentifierTypeLabelKeys.PlasticNumber)}</TableCell>
                        {/* Renders empty when the animal has no plastic number: no dash, no zero and
                            never the RFID/animal number above. */}
                        <TableCell sx={BOLD} id="animal-plastic-number">
                          {plasticNumber}
                        </TableCell>
                      </TableRow>

                      <TableRow key={'animalType'}>
                        <TableCell align="right">{t('f-com-animal-type')}</TableCell>
                        <TableCell sx={BOLD}>{AnimalType[animal.animalType]}</TableCell>
                      </TableRow>

                      <TableRow key={'animalDateOfBirth'}>
                        <TableCell align="right">{t('f-com-date-of-birth')}</TableCell>
                        <TableCell sx={BOLD}>{new Date(animal.dateOfBirth).toLocaleString()}</TableCell>
                      </TableRow>

                      {animal.location && (
                        <TableRow key={'animalLocation'}>
                          <TableCell align="right">{t('f-animal-view-location')}</TableCell>
                          <TableCell sx={BOLD}>
                            {<FontAwesomeIcon icon={faMapMarkerAlt} />} {renderLocation(animal.location)}
                          </TableCell>
                        </TableRow>
                      )}

                      <TableRow key={'animalGender'}>
                        <TableCell align="right">{t('f-gender')}</TableCell>
                        <TableCell sx={BOLD}>
                          {renderGenderIcon(animal.gender)} {AnimalGender[animal.gender]}
                        </TableCell>
                      </TableRow>

                      <TableRow key={'animalGenetics'}>
                        <TableCell align="right">{t('f-genetics')}</TableCell>
                        <TableCell sx={BOLD}>{animal.genetics}</TableCell>
                      </TableRow>

                      <TableRow key={'animalObservations'}>
                        <TableCell align="right">{t('f-animal-observations')}</TableCell>
                        <TableCell id="animal-observations">{renderObservationsCell()}</TableCell>
                      </TableRow>

                      <TableRow key={'animalWeight'}>
                        <TableCell align="right">{t('f-animal-view-weight')}</TableCell>
                        <TableCell sx={BOLD}>
                          {renderWeightCellDoubleWeightWithDate(
                            animal.weight?.birthed ?? null,
                            WeightType.Grams,
                            animal.weightCategory
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow key={'animalWeightCategory'}>
                        <TableCell align="right">{t('f-animal-weight-category')}</TableCell>
                        <TableCell sx={BOLD}>{t(AnimalWeightCategoryLabelKeys[animal.weightCategory])}</TableCell>
                      </TableRow>

                      <TableRow key={'animalWeanedWeight'}>
                        <TableCell align="right">{t('f-weaned-weight')}</TableCell>
                        <TableCell sx={BOLD}>
                          {(() => {
                            const convertedWeight = getConvertWeight(animal.weight?.weaned);
                            return convertedWeight
                              ? renderWeightCellDoubleWeightWithDate(
                                  convertedWeight,
                                  WeightType.Kilograms,
                                  animal.weightCategory
                                )
                              : '-';
                          })()}
                        </TableCell>
                      </TableRow>

                      <TableRow key={'animalWeanedDate'}>
                        <TableCell align="right">{t('f-weaned-date')}</TableCell>
                        <TableCell sx={BOLD}>
                          <Grid>{formattedDate(animal.weight?.weaned?.date ?? null)}</Grid>
                        </TableCell>
                      </TableRow>

                      <TableRow key={'nipples'}>
                        <TableCell align="right">{t('f-animal-nipples')}</TableCell>
                        <TableCell>{renderNipplesCell(animal)}</TableCell>
                      </TableRow>

                      <TableRow key={'antibiotic'}>
                        <TableCell align="right">{t('f-animal-antibiotic-free')}</TableCell>
                        <TableCell>{renderAntibioticFreeCell()}</TableCell>
                      </TableRow>

                      <TableRow key={'projectedSlaughter'}>
                        <TableCell align="right">{t('f-animal-projected-slaughter-date')}</TableCell>
                        <TableCell sx={BOLD}>
                          {projectedSlaughter ? (
                            <>
                              {projectedSlaughter.at}
                              {projectedSlaughter.isEstimated && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="textSecondary"
                                  style={{ marginLeft: 6 }}
                                >
                                  {t('f-animal-projected-slaughter-estimated')}
                                </Typography>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>

                <Grid item xs={12}>
                  <InjectionHistoryView entityId={id as string} onReload={() => loadAntibioticFreeStatus()} />
                </Grid>

                {animal.fosterMotherHistory && animal.fosterMotherHistory.length > 0 && (
                  <Grid item xs={12}>
                    <AnimalViewFosterMotherHistory
                      history={animal.fosterMotherHistory}
                      currentFosterMotherId={animal.fosterMotherId}
                    />
                  </Grid>
                )}

                {(animal.animalType === AnimalType.Boar || animal.animalType === AnimalType.Sow) && (
                  <Grid item xs={12}>
                    <AnimalViewPiglets animalId={id as string} animalType={animal.animalType} />
                  </Grid>
                )}

                {animal.animalType === AnimalType.Sow && (
                  <Grid item xs={12}>
                    <AnimalViewFosterMotherTransfers animalId={id as string} animalType={animal.animalType} />
                  </Grid>
                )}

                {(animal.animalType === AnimalType.Boar || animal.animalType === AnimalType.Sow) && (
                  <Grid item xs={12}>
                    <AnimalTrendData
                      animalId={id as string}
                      animalType={animal.animalType}
                      animalTrendDataType={animalTrendDataType}
                      onAnimalTrendDataTypeChanged={setAnimalTrendDataType}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    {animal.slaughterhouseData && (
                      <Grid item sm={6} xs={12}>
                        <AnimalViewSlaughterHouse slaughterhouseData={animal.slaughterhouseData} showTitle={true} />
                      </Grid>
                    )}

                    {animal.weight && (
                      <Grid item sm={6} xs={12}>
                        <AnimalViewWeightsAnalyticsGraph
                          weight={animal.weight}
                          weightAnalytics={animal.weightAnalytics}
                          showTitle={true}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {currentTab === 'digitalTwin' && hasDigitalTwin && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <DigitalTwinTabEntryCard t={t} onOpenForecast={scrollToDigitalTwinForecast} />
                </Grid>
                <Grid item xs={12} id="digital-twin-forecast-anchor">
                  <DigitalTwinModule animalId={id as string} />
                </Grid>
              </Grid>
            )}

            {currentTab === 'notes' && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <AnimalViewNotes
                    allowAdd
                    notes={sortedNotes}
                    onNoteAdd={(text) => addAnimalNote(text)}
                    onNoteDelete={(id) => deleteAnimalNote(id)}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 'locationHistory' && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <AnimalLocationHistoryTimeline animalId={id as string} />
                </Grid>
              </Grid>
            )}

            {currentTab === 'externalId' && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <AnimalViewExternalIds animalId={id as string} />
                </Grid>
              </Grid>
            )}

            {currentTab === 'health' && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <AnimalViewHealthLogs animalId={id as string} />
                </Grid>
              </Grid>
            )}

            {currentTab === 'feed' && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <AnimalFeedTab animalId={id as string} />
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
      </Grid>
    </Grid>
  );
};

export default AnimalView;

const BOLD = { fontWeight: 'bold' } as const;
